'use client';

/**
 * RealtimeConflictAlerts
 *
 * Displays live conflict alerts pushed via Supabase broadcast channel.
 * Rendered inside Operations Dashboard and Duty Assignment screens.
 */

import React, { useState } from 'react';
import { AlertTriangle, X, Radio, ChevronRight, BellOff } from 'lucide-react';
import Link from 'next/link';
import type { ConflictAlert } from '@/lib/hooks/useRealtimeManagerChannels';

interface RealtimeConflictAlertsProps {
  alerts: ConflictAlert[];
  onClear: (id: string) => void;
  onClearAll: () => void;
}

const severityConfig = {
  critical: {
    border: 'border-negative/30',
    iconBg: 'bg-negative/12 text-negative',
    badge: 'bg-negative/12 text-negative',
    badgeText: 'CRITICAL',
    ring: 'ring-1 ring-negative/25',
  },
  warning: {
    border: 'border-warning/30',
    iconBg: 'bg-warning/12 text-warning',
    badge: 'bg-warning/12 text-warning',
    badgeText: 'WARNING',
    ring: '',
  },
  info: {
    border: 'border-primary/20',
    iconBg: 'bg-primary/12 text-primary',
    badge: 'bg-primary/12 text-primary',
    badgeText: 'INFO',
    ring: '',
  },
};

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

export default function RealtimeConflictAlerts({
  alerts,
  onClear,
  onClearAll,
}: RealtimeConflictAlertsProps) {
  const [collapsed, setCollapsed] = useState(false);

  if (alerts.length === 0) return null;

  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <Radio size={14} className="text-negative animate-pulse" />
            <span className="text-xs font-700 text-foreground">Live Conflict Alerts</span>
            <span
              className={`text-[10px] font-700 uppercase tracking-wider px-2 py-0.5 rounded-full ${
                criticalCount > 0 ? 'bg-negative/12 text-negative' : 'bg-warning/12 text-warning'
              }`}
            >
              {alerts.length} active
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="text-[11px] text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            {collapsed ? 'Show' : 'Hide'}
          </button>
          <button
            onClick={onClearAll}
            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            <BellOff size={11} />
            Clear all
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {alerts.map((alert) => {
            const cfg = severityConfig[alert.severity];
            return (
              <div
                key={alert.id}
                className={`glass-card rounded-2xl border p-4 ${cfg.border} ${alert.isNew ? cfg.ring : ''} relative transition-all duration-300`}
              >
                {/* Dismiss */}
                <button
                  onClick={() => onClear(alert.id)}
                  className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors duration-150"
                  aria-label="Dismiss alert"
                >
                  <X size={11} className="text-muted-foreground" />
                </button>

                <div className="flex items-start gap-3 pr-5">
                  <div
                    className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${cfg.iconBg}`}
                  >
                    <AlertTriangle size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <span
                        className={`text-[9px] font-700 uppercase tracking-widest px-1.5 py-0.5 rounded-md ${cfg.badge}`}
                      >
                        {cfg.badgeText}
                      </span>
                      {alert.isNew && (
                        <span className="text-[9px] font-700 uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-foreground/8 text-foreground">
                          LIVE
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-700 text-foreground leading-snug">{alert.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{alert.detail}</p>
                    {(alert.dutyCode || alert.crewName || alert.route) && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {alert.dutyCode && (
                          <span className="text-[10px] font-600 px-1.5 py-0.5 rounded bg-foreground/6 text-foreground">
                            {alert.dutyCode}
                          </span>
                        )}
                        {alert.crewName && (
                          <span className="text-[10px] font-600 px-1.5 py-0.5 rounded bg-foreground/6 text-foreground">
                            {alert.crewName}
                          </span>
                        )}
                        {alert.route && (
                          <span className="text-[10px] font-600 px-1.5 py-0.5 rounded bg-foreground/6 text-foreground">
                            {alert.route}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] text-muted-foreground tabular-nums">
                        {formatTime(alert.timestamp)}
                      </span>
                      <Link
                        href="/duty-assignment"
                        className={`flex items-center gap-0.5 text-xs font-700 transition-colors duration-150 ${
                          alert.severity === 'critical' ?'text-negative hover:text-negative/80'
                            : alert.severity === 'warning' ?'text-warning hover:text-warning/80' :'text-primary hover:text-primary/80'
                        }`}
                      >
                        Resolve <ChevronRight size={11} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
